import { useCallback } from 'react'

import emDash from '~/components/emdash'

import { type SpeedUnit, isSpeedUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import type { UnitsFormat } from './units'
import { formatUnits, formatUnitsAbbr } from './units'

const speedFormats: UnitsFormat<SpeedUnit> = {
  kph: {
    long: 'kilometers per hour',
    short: 'km/h'
  },
  mph: {
    long: 'miles per hour',
    short: 'mph'
  }
}

export const formatSpeedUnits = formatUnits(speedFormats)
export const formatSpeedUnitsAbbr = formatUnitsAbbr(speedFormats)

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'kph',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

export const useSpeedFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: speed,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      if (!isSpeedUnit(units)) return invalidStr
      if (typeof speed !== 'string' && typeof speed !== 'number') return invalidStr

      // Coerce to number
      const speedNum = parseFloat(`${speed}`)
      if (isNaN(speedNum)) return invalidStr

      const speedStr = formatNumber(speedNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return speedStr

      const formattedUnits = longForm ? formatSpeedUnits(units) : formatSpeedUnitsAbbr(units)
      return [speedStr, formattedUnits].join(' ')
    },
    [formatNumber]
  )
}
